import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import TestimonialItem from "../TestimonialItem";
import styles from "./TestimonialSection.module.css";

/**
 * Auto-paging testimonial carousel (no arrows).
 * - Responsive perView: 1 (mobile), 2 (tablet), 3 (desktop) by default.
 * - Centers items whenever the visible slice has fewer than perView items.
 */
export const TestimonialSection = ({
  testimonials,
  autoPlayMs = 10000,
  itemsPerView = { base: 1, md: 2, lg: 3 },
}) => {
  const getPerView = () => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1920;
    if (typeof itemsPerView === "number") return itemsPerView;
    if (w >= 1200 && itemsPerView.lg) return itemsPerView.lg;
    if (w >= 768 && itemsPerView.md) return itemsPerView.md;
    return itemsPerView.base ?? 1;
  };

  const [perView, setPerView] = useState(getPerView());
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(testimonials.length / perView));
  const timerRef = useRef(null);
  const trackRef = useRef(null);

  // Keep CSS var in sync so columns/basis math works
  useEffect(() => {
    if (trackRef.current)
      trackRef.current.style.setProperty("--perView", perView);
  }, [perView]);

  // Handle resize and keep first visible item stable
  useEffect(() => {
    const onResize = () => {
      const next = getPerView();
      setPerView((prev) => {
        if (prev !== next) {
          const firstIdx = page * prev;
          const newPage = Math.floor(firstIdx / next);
          setPage(
            Math.min(
              newPage,
              Math.max(0, Math.ceil(testimonials.length / next) - 1)
            )
          );
        }
        return next;
      });
    };
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, testimonials.length]);

  // Autoplay
  useEffect(() => {
    if (totalPages <= 1) return;
    timerRef.current = setInterval(() => {
      setPage((p) => (p + 1) % totalPages);
    }, autoPlayMs);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [autoPlayMs, totalPages]);

  const startIdx = page * perView;
  const visible = useMemo(
    () => testimonials.slice(startIdx, startIdx + perView),
    [testimonials, startIdx, perView]
  );

  // Center if fewer than perView are visible (e.g., last page with only 1–2)
  const shouldCenter = visible.length < perView;

  return (
    <section className={styles.container} aria-label="Testimonials">
      <h4 className={styles.title}>What Pilots Are Saying!</h4>

      <div className={styles.viewport}>
        <ul
          ref={trackRef}
          className={`${styles.track} ${shouldCenter ? styles.centered : ""}`}
          role="list"
        >
          {visible.map((t, i) => (
            <li className={styles.slide} key={`${t.name}-${startIdx + i}`}>
              <div className={styles.cardWrap}>
                <TestimonialItem
                  quote={t.quote}
                  name={t.name}
                  company={t.company}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>

      {totalPages > 1 && (
        <div
          className={styles.dots}
          role="tablist"
          aria-label="Testimonial pages"
        >
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              type="button"
              className={`${styles.dot} ${i === page ? styles.dotActive : ""}`}
              aria-label={`Go to page ${i + 1}`}
              aria-selected={i === page}
              role="tab"
              onClick={() => setPage(i)}
            />
          ))}
        </div>
      )}
    </section>
  );
};

TestimonialSection.propTypes = {
  testimonials: PropTypes.arrayOf(
    PropTypes.shape({
      quote: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      company: PropTypes.string,
    })
  ).isRequired,
  autoPlayMs: PropTypes.number,
  itemsPerView: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      base: PropTypes.number,
      md: PropTypes.number,
      lg: PropTypes.number,
    }),
  ]),
};
